import {noop, pick} from '@benchling/js-utils';
import React from 'react';

import {Registry} from 'constants/RegistryConstants';
import {
  Batch,
  BatchSummary,
  InitialParentStorable,
  MODAL_PANE,
  MODAL_PANE_TO_TITLE,
  ModalPane,
  StorableModalCreateProps,
} from 'constants/SampleConstants';
import {SampleRestrictionStatus} from 'constants/SampleStatusConstants';
import {BioentitySummary, TagsDict} from 'folder-items/FileConstants';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import withContainableLoading from 'samples/containables/withContainableLoading';
import {CreateContainerParam} from 'samples/ContainerDAO';
import useSkippableStorableSummaryQuery from 'samples/storables/graphql/useSkippableStorableSummaryQuery';
import * as TagUtil from 'schemas/tags/TagUtil';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as ModalContainer from 'ui-library/ModalContainer';
import Modal from 'ui-library/modals/Modal';

import CreateStorableModal from '../CreateStorableModal';
import * as SampleActions from '../SampleActions';
import * as StorableActions from '../StorableActions';
import CreateContainerPane, {CreateContainersPaneState} from './CreateContainerPane';

export interface CreateContainerModalProps {
  disallowContainableEdit?: boolean;
  initialInputContainableId?: string;
  initialRegistry: Registry | null;
  initialParentStorableId: string | null;
  initialParentStorable: InitialParentStorable | null;
  onCreate?: (args: Array<string>) => void;
  onRequestClose?: () => void;
  shouldShowPlaceholderContainable?: boolean;
  shouldIncludeStorableSummaryExtrasOnRelist?: boolean;
  initialInputContainable?: Batch;
  actionOrigin: ActionOrigin;
}

class CreateContainerModal extends React.Component<CreateContainerModalProps> {
  static defaultProps = {
    shouldShowPlaceholderContainable: false,
    shouldIncludeStorableSummaryExtrasOnRelist: false,
    disallowContainableEdit: false,
  };

  private handleCreateContainers = (
    onFinish: (errorMessage?: string) => void,
    createPropsArray: Array<StorableModalCreateProps>,
    paneState: CreateContainersPaneState
  ): void => {
    if (createPropsArray.some(({tags}) => !TagUtil.areTagsValidAndNotify(tags))) {
      return;
    }

    const tagsDict: TagsDict = {};
    createPropsArray.forEach((createProps) => {
      TagUtil.tagUpdateObjectToTagsDict(TagUtil.getTagsToSet(createProps.tags), tagsDict);
    });

    const studyIds = pick(paneState, 'studyIds');

    const containerDatas: Array<CreateContainerParam> = createPropsArray.map(
      (createProps: StorableModalCreateProps) => ({
        containerSchemaId: createProps.selectedSchemaId,
        barcode: createProps.barcode,
        inputContainableParams: paneState.inputContainable
          ? {
              concentration: paneState.concentration,
              id: paneState.inputContainable.id,
              quantity: paneState.quantity,
            }
          : null,
        name: createProps.name, // Will get overwritten on the BE if a container name template is applied
        parentStorableData: createProps.parentStorableData,
        restrictionStatus: paneState.inputContainable
          ? paneState.restrictionStatus
          : SampleRestrictionStatus.UNRESTRICTED,
        sampleRoles: {
          sampleOwnerIds: paneState.inputContainable
            ? paneState.sampleRoles.sampleOwners.map((sampleOwner) => sampleOwner.id)
            : [],
          restrictedSampleUserIds:
            paneState.inputContainable && paneState.restrictionStatus === SampleRestrictionStatus.RESTRICTED
              ? paneState.sampleRoles.restrictedSampleUsers.map(
                  (restrictedSampleUser) => restrictedSampleUser.id
                )
              : [],
        },
        sourceId: createProps.sourceId,
        tags: tagsDict,
        ...studyIds,
      })
    );

    StorableActions.createContainers(containerDatas, {
      onCreate: (containers) => {
        const containerIds = containers.map(({id}) => id);
        if (this.props.onCreate) {
          this.props.onCreate(containerIds);
        }
        if (paneState.inputContainable) {
          SampleActions.relistContainersForContainable(paneState.inputContainable.id, {
            includeStorableSummaryExtras: this.props.shouldIncludeStorableSummaryExtrasOnRelist,
          });
        }
      },
      onFinish,
    });
  };

  override render(): JSX.Element {
    if (
      (this.props.initialInputContainableId && !this.props.initialInputContainable) ||
      (this.props.initialParentStorableId && !this.props.initialParentStorable)
    ) {
      return (
        <Modal title={MODAL_PANE_TO_TITLE[MODAL_PANE.CREATE_CONTAINERS]} onRequestClose={noop}>
          <CenteredHorizontalThrobber />
        </Modal>
      );
    }
    return (
      <CreateStorableModal
        initialInputContainable={this.props.initialInputContainable}
        initialParentStorable={this.props.initialParentStorable}
        initialRegistry={this.props.initialRegistry}
        modalPane={ModalPane.CREATE_CONTAINERS}
        onCreate={this.handleCreateContainers}
        onRequestClose={this.props.onRequestClose ?? noop}
        paneClass={CreateContainerPane}
        paneProps={{
          disallowContainableEdit: this.props.disallowContainableEdit!,
          shouldShowPlaceholderContainable: this.props.shouldShowPlaceholderContainable!,
        }}
        analyticsMetadata={{actionOrigin: this.props.actionOrigin}}
      />
    );
  }
}

const WrappedCreateContainerModal = withContainableLoading(
  CreateContainerModal,
  (props: CreateContainerModalProps) => props.initialInputContainableId,

  (containable: BatchSummary | BioentitySummary) => ({
    initialInputContainable: containable,
  })
);

function QueryingCreateContainerModal(
  props: Omit<CreateContainerModalProps, 'initialParentStorable'>
): JSX.Element {
  const initialParentStorable = useSkippableStorableSummaryQuery(props.initialParentStorableId);

  return <WrappedCreateContainerModal {...props} initialParentStorable={initialParentStorable} />;
}

export function close(): void {
  ModalContainer.close(QueryingCreateContainerModal);
}

export function open(props: Omit<CreateContainerModalProps, 'initialParentStorable'>): void {
  ModalContainer.open(
    QueryingCreateContainerModal,
    <QueryingCreateContainerModal {...props} onRequestClose={close} />
  );
}

export default QueryingCreateContainerModal;
